import { useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { PageLink, PageTitle } from '../../../_metronic/layout/core';
import {
  ListsWidget4,
  TablesWidget12,
  TablesWidget5,
  TablesWidget6,
  TablesWidget9,
} from '../../../_metronic/partials/widgets';
import { HolidayWidget } from '../../../_metronic/partials/widgets/lists/HolidayWidget';
import AttendanceListWidget from './../../../_metronic/partials/widgets/attendance-widget/AttendanceListWidget';
import AttendanceManagementList from '../../../_metronic/partials/widgets/attendance-widget/AttendanceManagementList';
import { permissionCheck } from '../../helper-functions/PermissionCheck';
import Loader from '../../../_metronic/layout/components/aside/Loader';
import EmployeeOverviewWidget from '../../../_metronic/partials/widgets/tables/TablesWidget7';
import "../../../_metronic/assets/keenicons/outline/style.css";
import Announcements from '../../../_metronic/partials/widgets/tables/Announcements';
import { useQuery } from 'react-query';
import { get_all_dashboard_data_list } from './api/dashboardApi';
import EmployeesLeaveTodayWidget from '../../../_metronic/partials/widgets/tables/EmployeesLeaveTodayWidget';
import { TimeAtWorkWidget } from '../../../_metronic/partials/widgets/time-at-work/components/TimeAtWorkWidget';
import { AttendanceFeedWidget } from '../../../_metronic/partials/widgets/lists/AttendanceFeedWidget';

const dashboardBreadCrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Dashboard',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
];

const DashboardPage = () => {
  const [permissions, setPermissions] = useState({
    employee_attendance_add: false,
    employee_attendance_edit: false,
    employee_attendance_delete: false,
    employee_attendance_log: false,
    employee_attendance_view: false,
    employee_leaves_add: false,
    employee_leaves_edit: false,
    employee_leaves_delete: false,
    employee_leaves_log: false,
    employee_leaves_view: false,
    employee_announcement_add: false,
    employee_announcement_edit: false,
    employee_announcement_delete: false,
    employee_announcement_log: false,
    employee_announcement_view: false,
  });
  
  const [loading, setLoading] = useState(true);
  const authData = useMemo(() => JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}'), []);
  
  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery('get_all_dashboard_data_list', get_all_dashboard_data_list, {
    refetchInterval:8000,
    refetchOnWindowFocus:false
  });

  const dashboardDataList = useMemo(() => dashboardData?.data || null, [dashboardData]);

  // Initialize component
  useEffect(() => {
    document.body.classList.add('aside-secondary-disabled', 'aside-secondary-enabled');
    localStorage.removeItem("pathMatched");
    localStorage.removeItem('pathMatchedEditEmployeeMenu');
    localStorage.removeItem('Employee_Name');
    localStorage.removeItem('current_user_id');
    
    document.getElementById('kt_layout_toolbar')?.classList.remove('d-none');
    
    return () => {
      document.getElementById('kt_layout_toolbar')?.classList.add('d-none');
    };
  }, []);

  // Set permissions
  useEffect(() => {
    setPermissions({
      employee_attendance_add: permissionCheck('employeeattendance-create'),
      employee_attendance_edit: permissionCheck('employeeattendance-update'),
      employee_attendance_delete: permissionCheck('employeeattendance-delete'),
      employee_attendance_view: permissionCheck('employeeattendance-view'),
      employee_attendance_log: permissionCheck('employeeattendance-activity-log'),
      employee_leaves_add: permissionCheck('employeeleaves-create'),
      employee_leaves_edit: permissionCheck('employeeleaves-update'),
      employee_leaves_delete: permissionCheck('employeeleaves-delete'),
      employee_leaves_view: permissionCheck('employeeleaves-view'),
      employee_leaves_log: permissionCheck('employeeleaves-activity-log'),
      employee_announcement_add: permissionCheck('announcement-create'),
      employee_announcement_edit: permissionCheck('announcement-update'),
      employee_announcement_delete: permissionCheck('announcement-delete'),
      employee_announcement_view: permissionCheck('announcement-view'),
      employee_announcement_log: permissionCheck('announcement-activity-log'),
    });
    
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  // Memoize permission checks to prevent unnecessary re-renders
  const hasAttendancePermissions = useMemo(() => (
    permissions.employee_attendance_add ||
    permissions.employee_attendance_delete ||
    permissions.employee_attendance_log ||
    permissions.employee_attendance_edit
  ), [permissions]);

  const hasLeavePermissions = useMemo(() => (
    permissions.employee_leaves_add ||
    permissions.employee_leaves_delete ||
    permissions.employee_leaves_edit ||
    permissions.employee_leaves_log
  ), [permissions]);

  const isSuperAdmin = useMemo(() => authData?.user?.is_superadmin === 1, [authData]);

  if (isLoading || loading) {
    return <Loader />;
  }

  if (isError) {
    return <div>Error occurred: {error instanceof Error ? error.message : "Unknown error"}</div>;
  }

  return (
    <div className="container-fluid">
      {/* Attendance Widgets */}
      <div className="row mb-5">
        <div className="col-lg-6 mb-4">
          <TimeAtWorkWidget className="card-stretch mb-5 shadow-sm" />
        </div>
        <div className="col-lg-6 mb-4">
          <TablesWidget12 
            employee_attendance_by_id={dashboardDataList?.employee_attendance_by_id || []} 
            className="card-stretch mb-5 shadow-sm" 
          />
        </div>
      </div>

      {/* Upcoming Birthday and Holidays Widgets */}
      <div className="row mb-5">
        <div className="col-lg-6 mb-4">
          <ListsWidget4 className="card-stretch mb-5 shadow-sm" />
        </div>
        <div className="col-lg-6 mb-4">
          <HolidayWidget className="card-stretch mb-5 shadow-sm" />
        </div>
      </div>

      <div className="row mb-5">
        <div className="col-lg-12 mb-4">
          <AttendanceFeedWidget className="card-stretch mb-5 shadow-sm" />
        </div>
      </div>

      {/* Leave Reports and Requests */}
      {hasLeavePermissions && (
        <div className="row mb-5">
          <div className="col-lg-12 mb-4">
            <TablesWidget9 className="card-xxl-stretch mb-5 mb-xl-10 shadow-sm" />
          </div>
        </div>
      )}

      {/* All employees leave approval and Employee Overview */}
      {isSuperAdmin && (
        <div className="row mb-5">
          <div className="col-lg-6 mb-4">
            <TablesWidget5 className="card-stretch mb-5 shadow-sm" />
          </div>
          <div className="col-lg-6 mb-4">
            <EmployeeOverviewWidget className="card-stretch mb-5 shadow-sm" />
          </div>
        </div>
      )}

      <div className="row mb-5">
        <div className="col-lg-12 mb-4">
          <EmployeesLeaveTodayWidget />
        </div>
      </div>

      {/* Announcements */}
      <div className="row mb-5">
        <div className="col-lg-12 mb-4">
          <Announcements />
        </div>
      </div>

      {/* Attendance Management Widgets */}
      {hasAttendancePermissions ? (
        <div className="row mb-5">
          <div className="col-lg-12 mb-4">
            <AttendanceManagementList  
              refetch={refetch} 
              employee_attendance={dashboardDataList?.employee_attendance || []} 
              active_users={dashboardDataList?.active_users || []} 
              location_shift={dashboardDataList?.location_shift || []} 
              active_attendance_type={dashboardDataList?.active_attendance_type || []} 
            />
          </div>
          <div className="col-lg-12 mb-4">
            <AttendanceListWidget 
              refetch={refetch} 
              employee_attendance_by_id={dashboardDataList?.employee_attendance_by_id || []} 
              location_shift={dashboardDataList?.location_shift || []} 
              active_attendance_type={dashboardDataList?.active_attendance_type || []} 
            />
          </div>
        </div>
      ) : (
        <div className="row mb-5">
          <div className="col-lg-12 mb-4">
            <AttendanceListWidget   
              refetch={refetch} 
              employee_attendance_by_id={dashboardDataList?.employee_attendance_by_id || []}  
              location_shift={dashboardDataList?.location_shift || []} 
              active_attendance_type={dashboardDataList?.active_attendance_type || []} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardWrapper = () => {
  const intl = useIntl();
  return (
    <>
      <PageTitle breadcrumbs={dashboardBreadCrumbs}>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>
      <DashboardPage />
    </>
  );
};

export { DashboardWrapper };